import React, { createContext, PropsWithChildren, useContext, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  DistributionTypeEnum,
  FixedPriceAcceptOfferInDTO,
  LocationTimeslotQuoteDemandOutDTO,
  MakeOfferInDTO,
  MakeOfferLocationSlotInDTO,
  MyQuoteInfoOutDTO,
} from '@api/ringil-api.v2';
import {
  apiPragueFromDayjsUtc,
  apiUtcFromDayjs
} from '@utils/dateWithTz.tsx';
import { ApiContext } from '@api/api';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { DemandDetailView } from '@app/pages/carrier/demand-detail/types.ts';
import { CreateSpotState } from '@app/pages/carrier/demand-detail/DemandDetailComponent.tsx';

interface DemandDetailContextType {
  BackendValidationComponent: any;
  state: DemandDetailView;
  rejectQuote: (customerId: string, applicationId: string, quoteId: string, type: DistributionTypeEnum) => void;
  makeQuote: (customerId: string, applicationId: string, quoteId: string, data: CreateSpotState, type: DistributionTypeEnum, onFinish?: () => void) => void;
  createQuote: (customerId: string, applicationId: string, quote: MyQuoteInfoOutDTO, timeslots: LocationTimeslotQuoteDemandOutDTO[]) => void;
  reconfirm: (companyId: string, applicationId: string, onSuccess?: () => void) => void;
  loading: boolean;
}

export const DemandDetailContext = createContext<DemandDetailContextType>(undefined);

interface ComponentProps {
  initialData: DemandDetailView;
}

const DemandDataProvider: React.FC<PropsWithChildren<ComponentProps>> = ({ initialData, children }) => {
  const [state, setState] = useImmer<DemandDetailView>(initialData);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const { caAcceptFixedPrice, caGetDemandDetail, caRejectFixedPrice, caMakeQuote, caCancelDemand, caMakeAnotherNewQuote, caReconfirmedExpedition } =
    useContext(ApiContext);

  const [loading, setLoading] = useState(false);

  const methods: DemandDetailContextType = {
    BackendValidationComponent,
    state,
    rejectQuote: (customerId: string, applicationId: string, quoteId: string, type: DistributionTypeEnum) => {
      switch (type) {
        case DistributionTypeEnum.SPOT:
          caCancelDemand(customerId, applicationId, quoteId, () => {
            caGetDemandDetail(customerId, applicationId, data => {
              setState(draft => data);
            });
          });
          break;
        case DistributionTypeEnum.FIXED_PRICE:
          caRejectFixedPrice(customerId, applicationId, quoteId, () => {
            caGetDemandDetail(customerId, applicationId, data => {
              setState(draft => data);
            });
          });
          break;
        default:
          // Do nothing
          break;
      }
    },
    makeQuote: (customerId: string, applicationId: string, quoteId: string, data: CreateSpotState, type: DistributionTypeEnum, onFinish?: () => void) => {
      switch (type) {
        case DistributionTypeEnum.SPOT: {
          const body: MakeOfferInDTO = {
            price: data.price,
            currency: data.currency,
            validTill: apiPragueFromDayjsUtc(data.utcEnd, 'Europe/Prague'),
            utcEnd: apiUtcFromDayjs(data.utcEnd),
            timezoneId: data.timezoneId,
            units: data.partialDeliveryUnits,
            note: data.note,
            transportLegs: data.transportLegs,
            timeslots: data.timeslots,
          };
          caMakeQuote(
            customerId,
            applicationId,
            quoteId,
            body,
            () => {
              caGetDemandDetail(customerId, applicationId, data => {
                setState(draft => data);
                onFinish();
              });
            },
            null,
            {
              onValidationFailed: v => {
                setBeValidationErrors(v);
                onFinish();
              },
            },
          );
          break;
        }
        case DistributionTypeEnum.FIXED_PRICE: {
          const body: FixedPriceAcceptOfferInDTO = {
            note: data.note,
            transportLegs: data.transportLegs,
          };
          caAcceptFixedPrice(
            customerId,
            applicationId,
            quoteId,
            body,
            () => {
              caGetDemandDetail(customerId, applicationId, data => {
                setState(draft => data);
                onFinish();
              });
            },
            null,
            {
              onValidationFailed: errors => {
                setBeValidationErrors(errors);
                onFinish();
              },
            },
          );
        }
      }

    },
    createQuote: (customerId: string, applicationId: string, quote: MyQuoteInfoOutDTO, timeslots: LocationTimeslotQuoteDemandOutDTO[]) => {
      const body: MakeOfferInDTO = {
        price: quote.myQuotePrice,
        currency: quote.myQuoteCurrency,
        validTill: quote.myQuoteValidTill,
        units: quote.myQuoteManipulationUnits,
        timeslots: timeslots.map(locationQueueSlot => {
          return {
            locationId: locationQueueSlot.locationId,
            useCustomerSlot: locationQueueSlot.timeslots.findIndex(a => a.isCarrier) < 0,
            differentTimeslots: locationQueueSlot.timeslots
              .filter(a => a.isCarrier)
              .map(s => {
                return {
                  timeslotSince: s.sinceInUTC,
                  timeslotTill: s.tillInUTC,
                  utcStart: s.utcStart,
                  utcEnd: s.utcEnd,
                  timezoneId: s.timezoneId,
                };
              }),
          } as MakeOfferLocationSlotInDTO;
        }),
        utcEnd: quote.utcEnd,
        timezoneId: quote.timezoneId,
      };

      caMakeAnotherNewQuote(customerId, applicationId, body, () => {
        caGetDemandDetail(customerId, applicationId, data => {
          setState(draft => data);
        });
      });
    },
    reconfirm: (companyId: string, applicationId: string, onSuccess: () => void = () => {}) => {
      caReconfirmedExpedition(companyId, applicationId, () => {
        caGetDemandDetail(companyId, applicationId, data => {
          setState(draft => data);
          onSuccess();
        });
      });
    },
    loading,
  };

  return <DemandDetailContext.Provider value={methods}>{children}</DemandDetailContext.Provider>;
};

export default DemandDataProvider;
